import React, { memo, useEffect, useMemo, useState, useCallback } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { OrthoImage } from "@nova/protobuf"
import { InspectorPanelAccordion } from "../accordion/InspectorPanelAccordion"
import { InspectorPanelAccordionKeyValueToggle } from "../accordion/InspectorPanelAccordionKeyValueToggle"
import { InspectorPanelAccordionSlider } from "../accordion/InspectorPanelAccordionSlider"
import { activateFlightPath, deactivateFlightPath } from "features/map/layers/slice"
import {
    selectActiveOrthophotos,
    selectOrthoOpacityById,
    selectOrthoOverlapLayerIsVisible,
    setOrthoOpacity,
    setOrthoOverlapLayer,
    toggleActiveOrthophoto,
    selectOrthoBoundingBoxForFlightId,
} from "plugins/Orthomosaic/OrthomosaicSlice"
import {
    activateOrthoComparison,
    coordinatesToComparisonBounds,
    type OrthoComparisonBounds,
} from "plugins/Orthomosaic/OrthoComparisonSlice"
import { ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useDebounceDispatch } from "tools/store"
import { isUsable, JobStatusType, ToolName } from "@nova/constants"
import { totalDistance } from "@nova/calculate-geospatial"
import { formatDistance } from "@nova/format"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useInspectorPanelOrtho } from "./useInspectorPanelOrtho"
import { AlertContainer } from "@nova/component-alert-container"
import { Hyperlink } from "@nova/component-typography"
import { getErrorObject } from "@nova/pymodule-error-handling"
import { InspectorPanelKeyValueDropdown } from "../InspectorPanelKeyValueDropdown"
import classNames from "classnames"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { ColorPalette } from "../icons/TODO-put-in-package/ColorPalette"
import { scanAreaFillColorSwatches, scanAreaOutlineColorSwatches } from "../constants"
import { useOptimisticLocalState } from "../useOptimisticLocalState"
import {
    selectFlightPathIsEnabled,
    selectInfraredFlightScanAreaOutlineColorById,
    selectOrthoFlightById,
    selectOrthoFlightScanAreaFillColorById,
    selectOrthoScanAreaIsEnabled,
} from "features/entity-caching/flights/selectors"
import {
    deactivateOrthoScanAreaLayerAction,
    activateOrthoScanAreaLayerAction,
} from "features/entity-caching/flights/slice"
import { selectOrthoImagesAndUploadsByFlightId } from "features/entity-caching/images/orthos/selectors"
import { updateOrthoFlight } from "features/pubsub/entityActions/flights/ortho"
import { selectOrthoMaskByFlightId } from "features/entity-caching/ortho-masks/selectors"
import { startMaskDrawing } from "plugins/Orthomosaic/OrthoMaskSlice"
import { updateOrthoMask } from "features/pubsub/entityActions/cubes/orthoMask"
import { setActiveTool } from "plugins/Toolbox/tool-registry"

const InspectorPanelOrthoLayer = memo(({ id }: { id: string }) => {
    const dispatch = useAppDispatch()
    const { debounceDispatch } = useDebounceDispatch(250)
    const flight = useAppSelector(selectOrthoFlightById(id))
    const [openCategories, setOpenCategories] = useState(new Set<string>())

    const flightPathIsEnabled = useAppSelector(selectFlightPathIsEnabled(id))
    const orthoOverlapIsEnabled = useAppSelector(selectOrthoOverlapLayerIsVisible(id))
    const orthomosaicIsEnabled = useAppSelector(selectActiveOrthophotos()).includes(id)
    const existingOpacity = useAppSelector(selectOrthoOpacityById(id))
    const orthoMask = useAppSelector(selectOrthoMaskByFlightId(id))
    const uploads = useAppSelector(selectOrthoImagesAndUploadsByFlightId(id))

    const flightBbox = useAppSelector(selectOrthoBoundingBoxForFlightId(id))

    const extraFeaturesJSON = flight?.job?.extraFeatures
    const scanAreaIsEnabled = useAppSelector(selectOrthoScanAreaIsEnabled(id))

    const [openDropdownId, setOpenDropdownId] = useState<string>()

    const [scanAreaFillColor, setScanAreaFillColor] = useOptimisticLocalState<number>(
        selectOrthoFlightScanAreaFillColorById(id),
        (scanAreaFillColor) => {
            if (!flight) return
            dispatch(updateOrthoFlight({ id: flight.id, updates: { scanAreaFillColor } }))
        },
        flight?.scanAreaFillColor || scanAreaFillColorSwatches[0]
    )
    const [scanAreaOutlineColor, setScanAreaOutlineColor] = useOptimisticLocalState<number>(
        selectInfraredFlightScanAreaOutlineColorById(id),
        (scanAreaOutlineColor) => {
            if (!flight) return
            dispatch(updateOrthoFlight({ id: flight.id, updates: { scanAreaOutlineColor } }))
        },
        flight?.scanAreaOutlineColor || scanAreaOutlineColorSwatches[0]
    )

    const [resizeTrigger, triggerResize] = useState(0)
    useEffect(() => {
        setTimeout(() => triggerResize((prev) => ++prev), 0)
    }, [openDropdownId, openCategories])

    const setCategoryIsOpen = useCallback((category: string, isOpen: boolean) => {
        setOpenCategories((prev) => {
            const openCategories = new Set(prev)
            if (isOpen) openCategories.add(category)
            else openCategories.delete(category)
            return openCategories
        })
    }, [])

    const { formattedOrthoArea } = useInspectorPanelOrtho(flight)

    const organizationSettingDistance = useAppSelector(selectWorkspaceDistanceUnit)
    const flightPathDistance = useMemo(() => {
        if (!uploads) return undefined
        const usableUploads = uploads.reduce((acc, cur) => {
            const upload = isUsable(cur)
            if (
                upload &&
                upload.takenAt &&
                upload.longitude !== undefined &&
                upload.latitude !== undefined
            )
                acc.push(upload as OrthoImage)
            return acc
        }, [] as OrthoImage[])

        const distance = totalDistance(
            usableUploads
                .sort((a, b) => (a.takenAt || 0) - (b.takenAt || 0))
                .map((image) => ({ longitude: image.longitude, latitude: image.latitude }))
        )
        return formatDistance({ value: distance, unit: organizationSettingDistance })
    }, [organizationSettingDistance, uploads])

    useEffect(() => {
        if (!flight?.visible) setOpenCategories(new Set())
    }, [flight?.visible])

    const flightError = useMemo(() => {
        if (!flight?.job?.error) return
        return getErrorObject(flight.job.error)
    }, [flight?.job?.error])

    if (!flight) return null

    const isJobReady = flight.job?.status ? flight.job.status >= JobStatusType.COMPLETED : false

    const handleActivateComparison = () => {
        // Use the displayed bounding box bounds so the divider line matches the visual bbox indicator
        const bounds: OrthoComparisonBounds | null = flightBbox
            ? {
                  minLng: flightBbox.bottomLeft[0],
                  maxLng: flightBbox.topRight[0],
                  minLat: flightBbox.bottomLeft[1],
                  maxLat: flightBbox.topRight[1],
              }
            : coordinatesToComparisonBounds(flight.orthoBoundingBox)
        if (!bounds) return
        dispatch(
            activateOrthoComparison({
                flightId: flight.id,
                bounds,
                threshold: (bounds.minLng + bounds.maxLng) / 2, // center, default direction "left"
            })
        )
    }

    return (
        <div className="flex flex-col gap-2 mb-2">
            {/* Flight Settings */}
            {uploads && uploads.length > 0 && (
                <InspectorPanelAccordion
                    title="Flight"
                    subtitle={flightPathDistance}
                    isOpen={openCategories.has("flight")}
                    setIsOpen={(isOpen) => setCategoryIsOpen("flight", isOpen)}
                    extraResizeDependency={resizeTrigger}
                    isToggled={flightPathIsEnabled && flight.visible}
                    setIsToggled={() => {
                        dispatch(
                            flightPathIsEnabled && flight.visible
                                ? deactivateFlightPath({ flightId: flight.id })
                                : activateFlightPath({ flightId: flight.id })
                        )
                        if (!flight.visible)
                            dispatch(
                                updateOrthoFlight({ id: flight.id, updates: { visible: true } })
                            )
                    }}
                >
                    {orthoOverlapIsEnabled !== undefined && (
                        <InspectorPanelAccordionKeyValueToggle
                            keyTitle="Overlap"
                            isToggled={orthoOverlapIsEnabled && flight.visible}
                            setIsToggled={() => {
                                dispatch(
                                    setOrthoOverlapLayer({
                                        id: flight.id,
                                        active: !orthoOverlapIsEnabled,
                                    })
                                )
                                if (!flight.visible)
                                    dispatch(
                                        updateOrthoFlight({
                                            id: flight.id,
                                            updates: { visible: true },
                                        })
                                    )
                            }}
                        />
                    )}

                    {isJobReady && !!extraFeaturesJSON?.includes("scanArea") && (
                        <>
                            <InspectorPanelAccordionKeyValueToggle
                                keyTitle="Scan Area"
                                isToggled={scanAreaIsEnabled && flight.visible}
                                setIsToggled={() => {
                                    dispatch(
                                        scanAreaIsEnabled && flight.visible
                                            ? deactivateOrthoScanAreaLayerAction({ id: flight.id })
                                            : activateOrthoScanAreaLayerAction({ id: flight.id })
                                    )
                                    if (!flight.visible)
                                        dispatch(
                                            updateOrthoFlight({
                                                id: flight.id,
                                                updates: { visible: true },
                                            })
                                        )
                                }}
                            />
                            <InspectorPanelKeyValueDropdown
                                id="scanarea-outline-color"
                                isOpen={openDropdownId === "scanarea-outline-color"}
                                setOpenDropdownId={setOpenDropdownId}
                                keyTitle="Outline"
                                Icon={Icons.BucketColor}
                                iconClasses={classNames({
                                    "text-gray-0": scanAreaIsEnabled && flight.visible,
                                    "text-gray-500": !(scanAreaIsEnabled && flight.visible),
                                })}
                                iconAccentColor={
                                    scanAreaIsEnabled && flight.visible
                                        ? decodeNumberToColor(scanAreaOutlineColor)
                                        : "#000"
                                }
                                disabled={!(scanAreaIsEnabled && flight.visible)}
                                className="ml-6"
                                includeSpacer
                                isInSubSection
                                align="right"
                            >
                                <ColorPalette
                                    swatches={scanAreaOutlineColorSwatches}
                                    selectedColor={scanAreaOutlineColor}
                                    setSelectedColor={setScanAreaOutlineColor}
                                    setOpenDropdownId={setOpenDropdownId}
                                    triggerResize={() => triggerResize((prev) => ++prev)}
                                />
                            </InspectorPanelKeyValueDropdown>
                            <InspectorPanelKeyValueDropdown
                                id="scanarea-fill-color"
                                isOpen={openDropdownId === "scanarea-fill-color"}
                                setOpenDropdownId={setOpenDropdownId}
                                keyTitle="Fill"
                                Icon={Icons.BucketColor}
                                iconClasses={classNames({
                                    "text-gray-0": scanAreaIsEnabled && flight.visible,
                                    "text-gray-500": !(scanAreaIsEnabled && flight.visible),
                                })}
                                iconAccentColor={
                                    scanAreaIsEnabled && flight.visible
                                        ? decodeNumberToColor(scanAreaFillColor)
                                        : "#000"
                                }
                                disabled={!(scanAreaIsEnabled && flight.visible)}
                                className="ml-6"
                                includeSpacer
                                isInSubSection
                                align="right"
                            >
                                <ColorPalette
                                    swatches={scanAreaFillColorSwatches}
                                    selectedColor={scanAreaFillColor}
                                    setSelectedColor={setScanAreaFillColor}
                                    setOpenDropdownId={setOpenDropdownId}
                                    triggerResize={() => triggerResize((prev) => ++prev)}
                                />
                            </InspectorPanelKeyValueDropdown>
                        </>
                    )}
                </InspectorPanelAccordion>
            )}

            {flight.job?.status === JobStatusType.FAILED && (
                <AlertContainer
                    type="error"
                    title={flightError?.message || "Processing Failed"}
                    body={
                        <div className="flex flex-col">
                            <small className="text-gray-0">
                                {flightError?.description ||
                                    "An unknown error occured. Please try again."}
                            </small>
                            <Hyperlink
                                title="Contact us for help"
                                href="mailto:help@novamaps.com"
                            />
                        </div>
                    }
                    className="mx-4"
                />
            )}

            {/* Orthomosaic Settings */}
            {isJobReady && (
                <InspectorPanelAccordion
                    title="Orthomosaic"
                    subtitle={formattedOrthoArea && `${formattedOrthoArea} coverage`}
                    isToggled={orthomosaicIsEnabled && flight.visible}
                    setIsToggled={() => {
                        if (flight.visible) dispatch(toggleActiveOrthophoto(flight.id))
                        setCategoryIsOpen("ortho", !orthomosaicIsEnabled || !flight.visible)
                        if (!flight.visible)
                            dispatch(
                                updateOrthoFlight({ id: flight.id, updates: { visible: true } })
                            )
                    }}
                    isOpen={openCategories.has("ortho")}
                    setIsOpen={(isOpen) => setCategoryIsOpen("ortho", isOpen)}
                    hasSubsections
                >
                    <div className="flex flex-col px-2.5 gap-1">
                        <div className="pb-2">
                            <InspectorPanelAccordionSlider
                                title="Opacity"
                                resetValue={100}
                                existingSliderValue={existingOpacity}
                                minSliderValue={1}
                                maxSliderValue={100}
                                onValueChanged={(opacity) =>
                                    debounceDispatch(
                                        setOrthoOpacity({ id: flight.id, updates: { opacity } })
                                    )
                                }
                            />
                        </div>
                        {!!orthoMask && (
                            <InspectorPanelAccordionKeyValueToggle
                                keyTitle="Crop"
                                isToggled={orthoMask.enabled}
                                setIsToggled={() =>
                                    dispatch(
                                        updateOrthoMask({
                                            id: orthoMask.id,
                                            updates: { enabled: !orthoMask.enabled },
                                        })
                                    )
                                }
                            />
                        )}
                    </div>
                    <div
                        className={classNames(
                            "flex pt-2 px-2.5 w-full border-t border-t-gray-700 text-gray-50 gap-1"
                        )}
                    >
                        {orthomosaicIsEnabled && flight.visible && (
                            <ButtonTertiary
                                title="Compare"
                                LeftIcon={Icons.RatioBar}
                                className="w-full"
                                onClick={handleActivateComparison}
                            />
                        )}
                        <ButtonTertiary
                            title="Crop"
                            LeftIcon={Icons.Crop}
                            className="w-full"
                            onClick={() => {
                                dispatch(setActiveTool(ToolName.OrthoMask))
                                dispatch(
                                    startMaskDrawing({
                                        flightId: flight.id,
                                        existingMask: orthoMask,
                                    })
                                )
                            }}
                        />
                    </div>
                </InspectorPanelAccordion>
            )}
        </div>
    )
})
InspectorPanelOrthoLayer.displayName = "InspectorPanelOrthoLayer"
export { InspectorPanelOrthoLayer }
